import { zodResolver } from '@hookform/resolvers/zod'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { conditionalRender } from '@repo/toolkit/conditional-render'
import Typography, { typography } from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { getEnrollmentEmails } from '../../../../api/enrollments/get-enrollment-emails'
import {
  MedicalAttestationTypeEnum,
  MedicalAttestationTypeEnumMapping
} from '../../../../lib/types/enums/medical-attestation-sending/medical-attestation-sending.enum'
import { MedicalAttestationSendingInput } from '../../../../lib/types/models/medical-attestation-sending/medical-attestation-sending'
import { BaseFormProps } from '../../../../lib/types/utils'
import { getSubmitAction } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { createEnumSchema } from '../../../../lib/utils/validations/enum-validation'
import { createStringSchema } from '../../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledInput from '../../../elements/inputs/controlled-input'
import ControlledRadioInput from '../../../elements/inputs/controlled-radio-input'
import ControlledSelectInput from '../../../elements/inputs/controlled-select-input'
import LoadingScreen from '../../../elements/loading/loading-screen'
import ValidationButtons from '../../../elements/validation-buttons'

type Props = BaseFormProps<MedicalAttestationSendingInput, true> & {
  enrollmentId: number
}

const formSchema: ZodType<MedicalAttestationSendingInput> = z.object({
  sentTo: createStringSchema('Destinataire'),
  type: createEnumSchema({
    fieldName: "Type d'attestation",
    enumType: MedicalAttestationTypeEnum
  }),
  content: createStringSchema('Contenu', { nullish: true }),
  contentEn: createStringSchema('Traduction', { nullish: true })
})

const SendMedicalStatementForm = ({
  enrollmentId,
  defaultValues,
  ...validationButtonProps
}: Props) => {
  const form = useForm<MedicalAttestationSendingInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })
  const content = form.watch('content')

  const { data: emails } = useQuery({
    queryKey: ['enrollment-emails', enrollmentId],
    queryFn: () => getEnrollmentEmails(enrollmentId)
  })

  if (!emails) return <LoadingScreen />

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={'flex gap-4 flex-col p-3'}>
          <FormField
            control={form.control}
            name={'sentTo'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    searchable
                    allowNewOption
                    label={'Destinataire'}
                    options={emails?.map((email) => ({ label: email, value: email }))}
                    selectedOption={conditionalRender(!!field.value, {
                      label: field.value,
                      value: field.value
                    })}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <div>
            <Typography variant={'label-semibold'}>Type d'attestation :</Typography>
            <div className={'h-1'} />
            <div className={'flex gap-10'}>
              {Object.values(MedicalAttestationTypeEnum).map((type) => (
                <FormField
                  key={type}
                  control={form.control}
                  name={'type'}
                  render={({ field }) => {
                    return (
                      <FormItem>
                        <FormControl>
                          <ControlledRadioInput
                            {...field}
                            label={MedicalAttestationTypeEnumMapping[type]}
                            checked={field.value === type}
                            onCheck={() => field.onChange(type)}
                          />
                        </FormControl>
                      </FormItem>
                    )
                  }}
                />
              ))}
            </div>
          </div>

          <FormField
            control={form.control}
            name={'content'}
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput
                    {...field}
                    type={'textarea'}
                    label={'Information complémentaire éventuelle'}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          {content?.trim() && (
            <FormField
              control={form.control}
              name={'contentEn'}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledInput
                      {...field}
                      type={'textarea'}
                      label={'Traduction'}
                      style={{
                        ...typography['small-medium'],
                        color: tailwindColors.grey.light.active
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          )}
        </div>

        <ValidationButtons {...validationButtonProps} hasBorderTop />
      </form>
    </Form>
  )
}

export default SendMedicalStatementForm
